// Analytics service entry point. UI/pipeline code goes through this module
// so no caller ever imports a concrete provider directly.

import type { AnalyticsProvider, TrackInput } from "./types";
import { stubAnalyticsProvider } from "./providers/stub";
import { posthogAnalyticsProvider } from "./providers/posthog";
import { plausibleAnalyticsProvider } from "./providers/plausible";
import { gaAnalyticsProvider } from "./providers/ga";

export type {
  AnalyticsEvent,
  AnalyticsProvider,
  AnalyticsProviderName,
  TrackInput,
} from "./types";

export function resolveAnalyticsProvider(): AnalyticsProvider {
  const kind = readProvider();
  switch (kind) {
    case "posthog":
      return posthogAnalyticsProvider;
    case "plausible":
      return plausibleAnalyticsProvider;
    case "ga":
      return gaAnalyticsProvider;
    case "stub":
    default:
      return stubAnalyticsProvider;
  }
}

/** Fire-and-forget event tracking. Never throws. */
export async function trackEvent(input: TrackInput): Promise<void> {
  const provider = resolveAnalyticsProvider();
  try {
    await provider.track(input);
  } catch (err) {
    const msg = err instanceof Error ? err.message : "unknown_error";
    console.error(`[analytics] provider ${provider.name} threw: ${msg}`);
  }
}

export async function identifyUser(
  userId: string,
  traits?: Record<string, unknown>,
): Promise<void> {
  const provider = resolveAnalyticsProvider();
  try {
    await provider.identify?.(userId, traits);
  } catch (err) {
    const msg = err instanceof Error ? err.message : "unknown_error";
    console.error(`[analytics] identify provider ${provider.name} threw: ${msg}`);
  }
}

function readProvider(): string {
  if (typeof process !== "undefined" && process.env?.ANALYTICS_PROVIDER)
    return process.env.ANALYTICS_PROVIDER.toLowerCase();
  if (typeof import.meta !== "undefined") {
    const v = (import.meta as { env?: Record<string, string | undefined> }).env
      ?.VITE_ANALYTICS_PROVIDER;
    if (v) return v.toLowerCase();
  }
  return "stub";
}
